import { Component, ElementRef, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { PendingReviewsBanner } from '../../shared/components/pending-reviews-banner/pending-reviews-banner';
import { Button } from '../../shared/components/button/button';
import { Guardrails as GuardrailsService, GuardrailDto, GuardrailResponse, GuardrailStatsDto } from '../../core/services/guardrails';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';

// Status values set by the backend (see GuardrailsController) — kept in sync with it.
const NEED_REVIEW_STATUS = 'needs_review';
const APPROVED_STATUS = 'Approved';
const DECLINED_STATUS = 'Declined';

// The API returns at most this many guardrails per request (GuardrailsController clamps to it).
const GUARDRAIL_FETCH_SIZE = 200;
// Rows per table page: the dropdown offers these, and any number up to the max can be typed.
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];
const MAX_PAGE_SIZE = 500;
// A bulk approve/decline sends one request per guardrail; keep a few in flight at a time.
const BULK_CONCURRENCY = 5;
const ALL_DOCUMENTS = 'All';

interface DocumentOption {
  id: string;
  name: string;
  count: number;
}

interface BulkOutcome {
  id: string;
  updated: GuardrailDto | null;
  error: any;
}

interface GuardrailRule {
  id: string;
  ruleName: string;
  policyName: string | null;
  documentId: string | null;
  documentName: string | null;
  classification: string | null;
  icon: string;
  category: string;
  categoryColor: string;
  confidence: number;
  decision: string;
  decisionColor: string;
  status: string;
  owner: string;
  lastUpdated: string;
}

function formatGuardrailStatus(status: string | null | undefined): string {
  if (!status) {
    return 'Pending';
  }

  switch (status) {
    case NEED_REVIEW_STATUS:
    case 'Needs Review':
      return 'Needs Review';
    case APPROVED_STATUS:
    case 'approved':
      return APPROVED_STATUS;
    case DECLINED_STATUS:
    case 'declined':
      return DECLINED_STATUS;
    default:
      return status;
  }
}

const CATEGORY_COLOR_MAP: Record<string, string> = {
  // legacy keys kept for backwards compatibility
  access_control: 'blue',
  approval: 'purple',
  retention: 'teal',
  disclosure: 'amber',
  compliance: 'green',
  security: 'red',
  // 12-term category enum from guardrail extraction pipeline
  approval_and_delegation: 'purple',
  access_and_security: 'red',
  financial_control: 'teal',
  procurement_and_vendor: 'amber',
  hr_and_conduct: 'blue',
  data_handling_and_privacy: 'teal',
  regulatory_compliance: 'green',
  operational_sop: 'amber',
  customer_and_sla: 'blue',
  legal_and_contractual: 'red',
  escalation: 'amber',
  tradition_and_norm: 'blue',
};

function categoryColor(ruleType: string): string {
  const key = (ruleType || '').toLowerCase().replace(/\s+/g, '_');
  return CATEGORY_COLOR_MAP[key] ?? 'blue';
}

function truncateStatement(s: string | null | undefined, max = 55): string {
  if (!s) return '';
  return s.length <= max ? s : s.slice(0, max).replace(/\s+\S*$/, '') + '…';
}

function toGuardrailRule(dto: GuardrailDto): GuardrailRule {
  const policyName = dto.policyName || null;
  return {
    id: dto.id,
    ruleName: policyName || truncateStatement(dto.normalizedStatement || dto.condition),
    policyName,
    documentId: dto.documentId || null,
    documentName: dto.documentName,
    classification: dto.classification || null,
    icon: 'shield',
    category: dto.ruleType,
    categoryColor: categoryColor(dto.ruleType),
    confidence: dto.confidence,
    decision: '',
    decisionColor: 'blue',
    status: formatGuardrailStatus(dto.status),
    owner: dto.reviewedBy || '',
    lastUpdated: dto.createdAt
      ? new Date(dto.createdAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
      : ''
  };
}

interface StatCard {
  label: string;
  value: string;
  delta: string;
  icon: 'shield' | 'shield-check' | 'shield-alert' | 'clock' | 'check' | 'lock' | 'alert' | 'percent';
  iconBg: string;
  iconColor: string;
  isWarning: boolean;
}

function buildStats(dto: GuardrailStatsDto | null): StatCard[] {
  const total = dto?.total ?? 0;
  const needsReviewCount = dto?.needsReviewCount ?? 0;
  const approvedCount = dto?.approvedCount ?? 0;
  const declinedCount = dto?.declinedCount ?? 0;
  const avgConfidencePercent = dto ? Math.round(Math.max(0, Math.min(1, Number(dto.averageConfidence) || 0)) * 100) : 0;

  return [
    { label: 'Total Guardrails', value: total.toLocaleString('en-US'), delta: '', icon: 'shield-check', iconBg: '#EFF6FF', iconColor: '#3B82F6', isWarning: false },
    { label: 'Needs Review', value: needsReviewCount.toLocaleString('en-US'), delta: '', icon: 'clock', iconBg: '#FFFBEB', iconColor: '#F59E0B', isWarning: true },
    { label: 'Approved', value: approvedCount.toLocaleString('en-US'), delta: '', icon: 'check', iconBg: '#F0FDF4', iconColor: '#22C55E', isWarning: false },
    { label: 'Declined', value: declinedCount.toLocaleString('en-US'), delta: '', icon: 'shield-alert', iconBg: '#FEF2F2', iconColor: '#EF4444', isWarning: true },
    { label: 'Avg. Confidence', value: `${avgConfidencePercent}%`, delta: '', icon: 'percent', iconBg: '#F5F3FF', iconColor: '#A855F7', isWarning: false }
  ];
}

@Component({
  selector: 'app-guardrails',
  standalone: true,
  imports: [CommonModule, Sidebar, Navbar, Button, ToastComponent, PendingReviewsBanner],
  templateUrl: './guardrails.html',
  styleUrl: './guardrails.css'
})
export class Guardrails implements OnInit {
  private guardrailsService = inject(GuardrailsService);
  private toast = inject(Toast);
  private elementRef = inject(ElementRef);

  // ⚠️ TEMP: dummy data — replace with real API data when guardrails endpoints are ready

  activeFilter = signal('All');
  filterTabs = ['All', 'Needs Review', 'Approved', 'Declined'];

  activeCategoryFilter = signal<string>('All');

  categoryOptions = computed<string[]>(() => {
    const cats = new Set<string>();
    for (const r of this.rules()) {
      if (r.category) cats.add(r.category);
    }
    return ['All', ...Array.from(cats).sort()];
  });

  setCategoryFilter(cat: string) {
    this.activeCategoryFilter.set(cat);
    this.selectedIds.set(new Set());
    this.currentPage.set(1);
  }

  // A bulk action acts on the selection, so switching document starts a fresh one: nothing picked
  // under another document stays selected out of sight.
  setDocumentFilter(documentId: string) {
    this.activeDocumentFilter.set(documentId);
    this.selectedIds.set(new Set());
    this.currentPage.set(1);
  }

  categoryColor(ruleType: string): string {
    return categoryColor(ruleType);
  }

  formatCategory(cat: string): string {
    if (cat === 'All') return 'All';
    const map: Record<string, string> = {
      // 12-term category enum from guardrail extraction pipeline
      APPROVAL_AND_DELEGATION: 'Approval & Delegation',
      ACCESS_AND_SECURITY: 'Access & Security',
      FINANCIAL_CONTROL: 'Financial Control',
      PROCUREMENT_AND_VENDOR: 'Procurement & Vendor',
      HR_AND_CONDUCT: 'HR & Conduct',
      DATA_HANDLING_AND_PRIVACY: 'Data & Privacy',
      REGULATORY_COMPLIANCE: 'Regulatory Compliance',
      OPERATIONAL_SOP: 'Operational SOP',
      CUSTOMER_AND_SLA: 'Customer & SLA',
      LEGAL_AND_CONTRACTUAL: 'Legal & Contractual',
      ESCALATION: 'Escalation',
      TRADITION_AND_NORM: 'Tradition & Norm',
      // legacy keys
      ACCESS_CONTROL: 'Access Control',
      COMPLIANCE_AND_REGULATION: 'Compliance & Reg.',
      DATA_GOVERNANCE: 'Data Governance',
      SECURITY: 'Security',
    };
    return map[cat] ?? cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  // Guardrail Library stat cards — backed by GET /api/guardrails/stats (see loadStats).
  statsDto = signal<GuardrailStatsDto | null>(null);
  isLoadingStats = signal(true);
  stats = computed<StatCard[]>(() => buildStats(this.statsDto()));

  // Guardrail Library table — backed by GET /api/guardrails (see loadGuardrails/toGuardrailRule).
  rules = signal<GuardrailRule[]>([]);
  // Full API rows (every field), kept alongside `rules` so the detail panel can show
  // everything the read-guardrails response returns, not just the mapped table columns.
  guardrailDtos = signal<GuardrailDto[]>([]);
  isLoadingRules = signal(true);
  isApproving = signal(false);
  isDeclining = signal(false);

  currentPage = signal(1);
  pageSize = signal(DEFAULT_PAGE_SIZE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly maxPageSize = MAX_PAGE_SIZE;
  // True once the user picks "Custom" in the page-size dropdown (the number box is then shown).
  private customPageSizeMode = signal(false);
  showCustomPageSize = computed(() => this.customPageSizeMode() || !PAGE_SIZE_OPTIONS.includes(this.pageSize()));

  // Document filter: the id of the chosen document, or ALL_DOCUMENTS.
  activeDocumentFilter = signal<string>(ALL_DOCUMENTS);

  // One entry per source document, with how many of its guardrails are in the current status tab.
  documentOptions = computed<DocumentOption[]>(() => {
    const names = new Map<string, string>();
    for (const r of this.rules()) {
      if (r.documentId && !names.has(r.documentId)) names.set(r.documentId, r.documentName || 'Unnamed document');
    }
    const inTab = this.getFilteredRules(this.rules(), this.activeFilter());
    return Array.from(names, ([id, name]) => ({
      id,
      name,
      count: inTab.filter(r => r.documentId === id).length
    })).sort((a, b) => a.name.localeCompare(b.name));
  });

  filteredRules = computed<GuardrailRule[]>(() => {
    const byStatus = this.getFilteredRules(this.rules(), this.activeFilter());
    const doc = this.activeDocumentFilter();
    const byDocument = doc === ALL_DOCUMENTS ? byStatus : byStatus.filter(r => r.documentId === doc);
    const cat = this.activeCategoryFilter();
    return cat === 'All' ? byDocument : byDocument.filter(r => r.category === cat);
  });

  totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRules().length / this.pageSize())));

  // Rows for the currently active page — bind this in the template instead of `rules()`
  pagedRules = computed<GuardrailRule[]>(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredRules().slice(start, start + this.pageSize());
  });

  // "11–20 of 42" shown beside the pager.
  rangeStart = computed(() => (this.filteredRules().length ? (this.currentPage() - 1) * this.pageSize() + 1 : 0));
  rangeEnd = computed(() => Math.min(this.currentPage() * this.pageSize(), this.filteredRules().length));

  // Page numbers with ellipsis for large lists, e.g. [1, '...', 4, 5, 6, '...', 20]
  pageNumbers = computed<(number | '...')[]>(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const pages: (number | '...')[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }

    pages.push(1);
    if (current > 3) pages.push('...');

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (current < total - 2) pages.push('...');
    pages.push(total);

    return pages;
  });

  goToPage(page: number | '...') {
    if (page === '...') return;
    if (page < 1 || page > this.totalPages()) return;
    this.currentPage.set(page);
  }

  prevPage() {
    this.goToPage(this.currentPage() - 1);
  }

  nextPage() {
    this.goToPage(this.currentPage() + 1);
  }

  // Rows per page. Ignores anything that isn't a whole number >= 1 and caps the rest at the max.
  setPageSize(raw: string | number) {
    const size = Math.floor(Number(raw));
    if (!Number.isFinite(size) || size < 1) return;
    this.pageSize.set(Math.min(size, MAX_PAGE_SIZE));
    this.currentPage.set(1);
  }

  onPageSizeSelect(value: string) {
    if (value === 'custom') {
      this.customPageSizeMode.set(true);
      return;
    }
    this.customPageSizeMode.set(false);
    this.setPageSize(value);
  }

  onPageSizeTyped(input: HTMLInputElement) {
    this.setPageSize(input.value);
    input.value = String(this.pageSize()); // snap back if the entry was empty, invalid or over the max
  }

  selectedIds = signal<Set<string>>(new Set());
  selectedCount = computed(() => this.selectedIds().size);
  // An approved guardrail can't be declined, so a bulk decline only acts on the others.
  declinableIds = computed(() =>
    this.rules().filter(r => this.selectedIds().has(r.id) && !this.isApproved(r.status)).map(r => r.id)
  );
  // Approving an already-approved guardrail would only re-send it to the vector DB.
  approvableIds = computed(() =>
    this.rules().filter(r => this.selectedIds().has(r.id) && !this.isApproved(r.status)).map(r => r.id)
  );

  viewedRuleId = signal<string | null>(null);

  viewedGuardrail = computed<GuardrailDto | null>(() =>
    this.guardrailDtos().find(g => g.id === this.viewedRuleId()) ?? null
  );
  // ⚠️ TEMP END

  ngOnInit() {
    this.loadGuardrails();
    this.loadStats();
  }

  loadStats() {
    this.isLoadingStats.set(true);
    this.guardrailsService.getStats().subscribe({
      next: (res) => {
        this.isLoadingStats.set(false);
        this.statsDto.set(res.data);
      },
      error: (err) => {
        this.isLoadingStats.set(false);
        const message = err?.error?.message || 'Failed to load guardrail stats.';
        this.toast.show(message, 'error');
      }
    });
  }

  // The API serves at most GUARDRAIL_FETCH_SIZE per request, so fetch every page: paging and
  // "select all" work on the full list, not just the first batch.
  private fetchAllGuardrails(): Observable<GuardrailDto[]> {
    return this.guardrailsService.readGuardrails(1, GUARDRAIL_FETCH_SIZE).pipe(
      switchMap(first => {
        const items = first.data.items || [];
        const pageCount = Math.ceil((first.data.total || 0) / GUARDRAIL_FETCH_SIZE);
        if (pageCount <= 1) return of(items);
        const rest = Array.from({ length: pageCount - 1 }, (_, i) =>
          this.guardrailsService.readGuardrails(i + 2, GUARDRAIL_FETCH_SIZE)
        );
        return forkJoin(rest).pipe(map(pages => [...items, ...pages.flatMap(p => p.data.items || [])]));
      })
    );
  }

  // resetPage=false reloads in place (used after a bulk action) and keeps the user on their page.
  loadGuardrails(resetPage = true) {
    if (resetPage) this.isLoadingRules.set(true);
    this.fetchAllGuardrails().subscribe({
      next: (items) => {
        this.isLoadingRules.set(false);
        this.guardrailDtos.set(items);
        this.rules.set(items.map(toGuardrailRule));
        const doc = this.activeDocumentFilter();
        if (doc !== ALL_DOCUMENTS && !this.documentOptions().some(o => o.id === doc)) {
          this.activeDocumentFilter.set(ALL_DOCUMENTS);
        }
        if (resetPage) {
          this.currentPage.set(1);
        } else {
          this.currentPage.update(page => Math.min(page, this.totalPages()));
        }
      },
      error: (err) => {
        this.isLoadingRules.set(false);
        const message = err?.error?.message || 'Failed to load guardrails.';
        this.toast.show(message, 'error');
      }
    });
  }

  viewDetail(id: string) {
    this.viewedRuleId.set(id);
  }

  closeDetail() {
    this.viewedRuleId.set(null);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.viewedRuleId()) return;

    const target = event.target as HTMLElement;
    const host = this.elementRef.nativeElement as HTMLElement;
    const rightColumn = host.querySelector('.right-column');
    const table = host.querySelector('.rules-table');

    if (rightColumn?.contains(target) || table?.contains(target)) return;

    this.closeDetail();
  }

  isViewed(id: string): boolean {
    return this.viewedRuleId() === id;
  }

  toggleSelect(id: string, event?: Event) {
    event?.stopPropagation();
    this.selectedIds.update(current => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  setActiveFilter(filter: string) {
    this.activeFilter.set(filter);
    this.activeCategoryFilter.set('All');
    this.selectedIds.set(new Set());
    this.currentPage.set(1);
  }

  tabLabel(tab: string): string {
    return tab === 'Needs Review' ? 'Need Review' : tab;
  }

  getFilteredRules(rules: GuardrailRule[], filter: string): GuardrailRule[] {
    switch (filter) {
      case 'Needs Review':
        return rules.filter(rule => this.displayStatus(rule.status) === 'Needs Review');
      case 'Approved':
        return rules.filter(rule => this.displayStatus(rule.status) === 'Approved');
      case 'Declined':
        return rules.filter(rule => this.displayStatus(rule.status) === 'Declined');
      default:
        return rules;
    }
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  isReviewed(status: string | null): boolean {
    const normalizedStatus = this.displayStatus(status);
    return normalizedStatus === APPROVED_STATUS || normalizedStatus === DECLINED_STATUS;
  }

  isApproved(status: string | null | undefined): boolean {
    return this.displayStatus(status) === APPROVED_STATUS;
  }

  isDeclined(status: string | null | undefined): boolean {
    return this.displayStatus(status) === DECLINED_STATUS;
  }

  formatDateTime(iso: string | null): string {
    return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';
  }

  // The header checkbox works on everything the current filters match (tab, document, category),
  // on every page — never just the rows on screen, so it doesn't depend on the page size.
  allFilteredSelected = computed(() => {
    const filtered = this.filteredRules();
    return filtered.length > 0 && filtered.every(r => this.selectedIds().has(r.id));
  });

  // Some, but not all, of the filtered guardrails are selected (shown as a dash).
  someFilteredSelected = computed(() =>
    !this.allFilteredSelected() && this.filteredRules().some(r => this.selectedIds().has(r.id))
  );

  toggleSelectAll(event?: Event) {
    event?.stopPropagation();
    if (!this.allFilteredSelected()) {
      this.selectAllFiltered();
      return;
    }
    const filteredIds = new Set(this.filteredRules().map(r => r.id));
    this.selectedIds.update(current => new Set([...current].filter(id => !filteredIds.has(id))));
  }

  selectAllFiltered() {
    this.selectedIds.update(current => {
      const next = new Set(current);
      this.filteredRules().forEach(r => next.add(r.id));
      return next;
    });
  }

  clearSelection() {
    this.selectedIds.set(new Set());
  }

  displayStatus(status: string | null | undefined): string {
    return formatGuardrailStatus(status);
  }

  getConfidencePercent(confidence: unknown): number {
    const numericValue = Number(confidence);
    if (!Number.isFinite(numericValue)) {
      return 0;
    }

    const clampedValue = Math.max(0, Math.min(1, numericValue));
    return Math.round(clampedValue * 100);
  }

  private applyUpdatedGuardrail(updated: GuardrailDto) {
    this.guardrailDtos.update(list => list.map(g => (g.id === updated.id ? updated : g)));
    this.rules.update(list => list.map(r => (r.id === updated.id ? toGuardrailRule(updated) : r)));
  }

  approveGuardrail(id: string) {
    this.isApproving.set(true);
    this.guardrailsService.approveGuardrail(id).subscribe({
      next: (res) => {
        this.isApproving.set(false);
        this.applyUpdatedGuardrail(res.data);
        this.loadStats();
        this.toast.show('Guardrail approved.', 'success');
      },
      error: (err) => {
        this.isApproving.set(false);
        const message = err?.error?.message || 'Failed to approve guardrail.';
        this.toast.show(message, 'error');
      }
    });
  }

  declineGuardrail(id: string) {
    this.isDeclining.set(true);
    this.guardrailsService.declineGuardrail(id).subscribe({
      next: (res) => {
        this.isDeclining.set(false);
        this.applyUpdatedGuardrail(res.data);
        this.loadStats();
        this.toast.show('Guardrail declined.', 'success');
      },
      error: (err) => {
        this.isDeclining.set(false);
        const message = err?.error?.message || 'Failed to decline guardrail.';
        this.toast.show(message, 'error');
      }
    });
  }

  // Runs one request per id, a few at a time. A failed request is reported but does not
  // discard the ones that succeeded, so the list always reflects what the server did.
  private runBulk(ids: string[], call: (id: string) => Observable<GuardrailResponse>): Observable<BulkOutcome[]> {
    return from(ids).pipe(
      mergeMap(
        id => call(id).pipe(
          map((res): BulkOutcome => ({ id, updated: res.data, error: null })),
          catchError(error => of<BulkOutcome>({ id, updated: null, error }))
        ),
        BULK_CONCURRENCY
      ),
      toArray()
    );
  }

  // Applies the successful results, keeps the failed ones selected so they can be retried, then
  // reloads the list so every tab shows each guardrail's real status.
  private finishBulk(outcomes: BulkOutcome[], doneLabel: string, note = '') {
    outcomes.forEach(o => o.updated && this.applyUpdatedGuardrail(o.updated));
    const failed = outcomes.filter(o => !o.updated);
    const done = outcomes.length - failed.length;
    this.selectedIds.set(new Set(failed.map(o => o.id)));
    this.loadStats();
    this.loadGuardrails(false);

    // The toast shows one message at a time, so a partial failure is reported in a single message.
    const doneText = `${done} guardrail${done === 1 ? '' : 's'} ${doneLabel}.${note}`;
    if (failed.length === 0) {
      this.toast.show(doneText, 'success');
      return;
    }
    const reason = failed[0].error?.error?.message || 'Please try again.';
    const failedText = `${failed.length} could not be ${doneLabel}: ${reason}`;
    this.toast.show(done > 0 ? `${doneText} ${failedText}` : failedText, 'error');
  }

  approveSelected() {
    const ids = this.approvableIds();
    if (!ids.length) return;
    const skippedApproved = this.selectedIds().size - ids.length;

    this.isApproving.set(true);
    this.runBulk(ids, id => this.guardrailsService.approveGuardrail(id)).subscribe(outcomes => {
      this.isApproving.set(false);
      this.finishBulk(outcomes, 'approved', skippedApproved > 0 ? ` ${skippedApproved} already approved.` : '');
    });
  }

  declineSelected() {
    const ids = this.declinableIds();
    if (!ids.length) return;
    const skippedApproved = this.selectedIds().size - ids.length;

    this.isDeclining.set(true);
    this.runBulk(ids, id => this.guardrailsService.declineGuardrail(id)).subscribe(outcomes => {
      this.isDeclining.set(false);
      this.finishBulk(
        outcomes,
        'declined',
        skippedApproved > 0 ? ` ${skippedApproved} approved guardrail(s) can't be declined and were skipped.` : ''
      );
    });
  }
}
